import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, BellOff, Send } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const supported = () =>
  "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;

export function NotificationSection() {
  const [enabled, setEnabled] = useState(false);
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/push/status");
      setDevices(data.devices);
      if (supported()) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub);
      }
    } catch (_) {}
  };

  useEffect(() => { loadStatus(); }, []);

  const enable = async () => {
    setMsg(null);
    if (!supported()) {
      setMsg({ ok: false, text: "This browser does not support push notifications" });
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setMsg({ ok: false, text: "Notifications were blocked — allow them in your browser settings" });
        return;
      }
      const { data } = await api.get("/push/vapid-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setEnabled(true);
      setMsg({ ok: true, text: "Daily briefing enabled on this device" });
      loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not enable notifications" });
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setMsg(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setEnabled(false);
      setMsg({ ok: true, text: "Notifications off on this device" });
      loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: "Could not disable" });
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setMsg(null);
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      setMsg({ ok: data.ok, text: data.ok ? "Test notification sent — check this device" : "Send failed — try re-enabling" });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not send test" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hud-frame p-6 space-y-5" data-testid="settings-push">
      <div className="kpi-label flex items-center gap-2"><Bell size={11} /> DAILY BRIEFING PUSH</div>
      <p className="text-sm text-muted-foreground">
        Every morning at 8:00 AM — your top 3 tasks for the day, delivered straight to this
        device. Works on desktop and on your phone once the app is installed.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        {enabled ? (
          <>
            <button onClick={disable} disabled={busy} className="hud-btn flex items-center gap-2" data-testid="push-disable">
              <BellOff size={11} /> Turn off on this device
            </button>
            <button onClick={sendTest} disabled={busy} className="hud-btn hud-btn-primary flex items-center gap-2" data-testid="push-test">
              <Send size={11} /> Send test now
            </button>
          </>
        ) : (
          <button onClick={enable} disabled={busy} className="hud-btn hud-btn-primary flex items-center gap-2" data-testid="push-enable">
            <Bell size={11} /> Enable daily briefing
          </button>
        )}
        {devices > 0 && (
          <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="push-devices">
            {devices} device{devices === 1 ? "" : "s"} subscribed
          </span>
        )}
      </div>
      {msg && (
        <div className={`mono text-[11px] uppercase tracking-widest ${msg.ok ? "text-oud" : "text-[hsl(var(--oxblood))]"}`} data-testid="push-msg">
          {msg.text}
        </div>
      )}
    </section>
  );
}
